import React, { useState, lazy } from 'react';
import { FadeIn } from './FadeIn';
import { MapPin, Phone, Mail, Clock, Send, MessageCircle } from 'lucide-react';
export function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    batch: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        batch: '',
        message: ''
      });
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }, 1500);
  };
  return <section id="contact" className="py-24 bg-yoga-warm" data-id="element-95">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-96">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-97">
          <FadeIn data-id="element-98">
            <span className="text-yoga-primary font-bold uppercase tracking-wider text-sm block mb-2" data-id="element-99">
              Get in Touch
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-yoga-dark mb-6" data-id="element-100">
              Start your yoga journey today
            </h2>
            <p className="text-lg text-yoga-muted" data-id="element-101">
              Have questions or ready to book your first class? Reach out to us
              using the form below or drop by our studio.
            </p>
          </FadeIn>
        </div>

        <div className="flex flex-col lg:flex-row gap-12 bg-white rounded-3xl shadow-xl overflow-hidden" data-id="element-102">
          {/* Contact Info & Map Side */}
          <div className="w-full lg:w-5/12 bg-yoga-dark text-white p-10 flex flex-col justify-between relative overflow-hidden" data-id="element-103">
            {/* Decorative pattern */}
            <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 rounded-full bg-white/5 blur-3xl" data-id="element-104"></div>
            <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-48 h-48 rounded-full bg-yoga-primary/20 blur-3xl" data-id="element-105"></div>

            <div className="relative z-10" data-id="element-106">
              <h3 className="text-3xl font-bold mb-8" data-id="element-107">Contact Information</h3>

              <div className="space-y-6 mb-10" data-id="element-108">
                <div className="flex items-start gap-4" data-id="element-109">
                  <MapPin className="w-6 h-6 text-yoga-primary shrink-0 mt-1" data-id="element-110" />
                  <div data-id="element-111">
                    <h4 className="font-bold text-lg mb-1" data-id="element-112">Studio Address</h4>
                    <p className="text-gray-300 leading-relaxed" data-id="element-113">
                      Sr 281, Anand Nagar, Lane 7, Kutwal Colony, Lohegaon,
                      Pune, Maharashtra 411047, India
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-114">
                  <Phone className="w-6 h-6 text-yoga-primary shrink-0 mt-1" data-id="element-115" />
                  <div data-id="element-116">
                    <h4 className="font-bold text-lg mb-1" data-id="element-117">Phone</h4>
                    <p className="text-gray-300" data-id="element-118">+91 87932 68867</p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-119">
                  <Clock className="w-6 h-6 text-yoga-primary shrink-0 mt-1" data-id="element-120" />
                  <div data-id="element-121">
                    <h4 className="font-bold text-lg mb-1" data-id="element-122">Operating Hours</h4>
                    <p className="text-gray-300" data-id="element-123">
                      Mon - Sat: Opens 6:00 AM
                      <br data-id="element-124" />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>

              <a href="https://wa.me/918793268867" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-full gap-2 bg-[#25D366] text-white py-4 rounded-xl font-bold hover:bg-[#128C7E] transition-colors" data-id="element-125">
                <MessageCircle className="w-5 h-5" data-id="element-126" /> Chat on WhatsApp
              </a>
            </div>

            {/* Map iframe */}
            <div className="mt-10 rounded-xl overflow-hidden h-48 relative z-10 border border-white/10" data-id="element-127">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1m3!1d3781.936082496515!2d73.9161743!3d18.576882!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2c147b8b3a3bf%3A0x6f0af115146d0c8!2sShree%20Yoga%20Fitness!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin" width="100%" height="100%" style={{
              border: 0
            }} allowFullScreen={false} loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Shree Yoga Fitness Location" data-id="element-128"></iframe>
            </div>
          </div>

          {/* Form Side */}
          <div className="w-full lg:w-7/12 p-10" data-id="element-129">
            <h3 className="text-2xl font-bold text-yoga-dark mb-6" data-id="element-130">
              Send us a message
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-131">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-132">
                <div data-id="element-133">
                  <label htmlFor="name" className="block text-sm font-bold text-yoga-dark mb-2" data-id="element-134">
                    Full Name *
                  </label>
                  <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-yoga-primary focus:ring-2 focus:ring-yoga-primary/20 outline-none transition-all bg-gray-50 focus:bg-white" placeholder="John Doe" data-id="element-135" />
                </div>
                <div data-id="element-136">
                  <label htmlFor="phone" className="block text-sm font-bold text-yoga-dark mb-2" data-id="element-137">
                    Phone Number *
                  </label>
                  <input type="tel" id="phone" name="phone" required value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-yoga-primary focus:ring-2 focus:ring-yoga-primary/20 outline-none transition-all bg-gray-50 focus:bg-white" placeholder="+91 98765 43210" data-id="element-138" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-139">
                <div data-id="element-140">
                  <label htmlFor="email" className="block text-sm font-bold text-yoga-dark mb-2" data-id="element-141">
                    Email Address
                  </label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-yoga-primary focus:ring-2 focus:ring-yoga-primary/20 outline-none transition-all bg-gray-50 focus:bg-white" placeholder="john@example.com" data-id="element-142" />
                </div>
                <div data-id="element-143">
                  <label htmlFor="batch" className="block text-sm font-bold text-yoga-dark mb-2" data-id="element-144">
                    Preferred Batch
                  </label>
                  <select id="batch" name="batch" value={formData.batch} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-yoga-primary focus:ring-2 focus:ring-yoga-primary/20 outline-none transition-all bg-gray-50 focus:bg-white appearance-none" data-id="element-145">
                    <option value="" data-id="element-146">Select a batch</option>
                    <option value="morning" data-id="element-147">Morning (6 AM - 8 AM)</option>
                    <option value="evening" data-id="element-148">Evening (5 PM - 7 PM)</option>
                    <option value="trial" data-id="element-149">Just a Trial Class</option>
                  </select>
                </div>
              </div>

              <div data-id="element-150">
                <label htmlFor="message" className="block text-sm font-bold text-yoga-dark mb-2" data-id="element-151">
                  Your Message
                </label>
                <textarea id="message" name="message" rows={4} value={formData.message} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-yoga-primary focus:ring-2 focus:ring-yoga-primary/20 outline-none transition-all bg-gray-50 focus:bg-white resize-none" placeholder="Tell us about your fitness goals or any specific questions..." data-id="element-152"></textarea>
              </div>

              <button type="submit" disabled={isSubmitting} className="w-full bg-yoga-primary text-white py-4 rounded-xl font-bold text-lg hover:bg-yoga-light transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-md" data-id="element-153">
                {isSubmitting ? 'Sending...' : <>
                    Send Message <Send className="w-5 h-5" data-id="element-154" />
                  </>}
              </button>

              {submitStatus === 'success' && <div className="p-4 bg-green-50 text-green-700 rounded-xl border border-green-200 text-center font-medium" data-id="element-155">
                  Thank you! Your message has been sent successfully. We'll get
                  back to you soon.
                </div>}
            </form>
          </div>
        </div>
      </div>
    </section>;
}